// StoriesSection organism component
import { useState, useEffect } from "react";
import { Heading } from "../atoms";
import { StoryCard } from "../molecules";
import { storyService } from "../../services/storyService";
import { resolveImageUrl } from "../../utils/imageUrl";
import { useLandingContent } from "../../contexts/LandingContentContext";

export const StoriesSection = () => {
  const { stories: storiesContent } = useLandingContent();
  const [stories, setStories] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const data = await storyService.getPublishedStories();
        setStories(data);
      } catch (error) {
        console.error("Error fetching stories:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  return (
    <section id="stories" className="py-24 bg-cream">
      <div className="max-w-6xl mx-auto px-6">
        {/* Editorial heading block */}
        <div className="text-center mb-16">
          <p className="font-display text-terracotta-600 text-sm uppercase tracking-widest mb-3">
            {storiesContent.eyebrow}
          </p>
          <Heading
            level={2}
            className="font-display text-ink text-4xl md:text-5xl leading-tight mb-4"
          >
            {storiesContent.heading}
          </Heading>
          <div className="mx-auto w-16 h-px bg-terracotta-300 mt-6" />
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-4 py-16 text-ink-soft">
            <div className="w-10 h-10 rounded-full border-2 border-terracotta-300 border-t-terracotta-600 animate-spin" />
            <p className="font-body text-base tracking-wide">
              Loading stories…
            </p>
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-display text-ink-soft text-xl italic">
              No stories available yet.
            </p>
            <p className="font-body text-ink-soft text-sm mt-2">
              Check back soon — new testimonies are on the way.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {stories.map((story) => (
              <StoryCard
                key={story.id}
                story={{
                  title: story.title,
                  desc: story.description,
                  img: resolveImageUrl(story.imageUrl),
                }}
                onClick={() =>
                  setSelectedImage(resolveImageUrl(story.imageUrl))
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* Image Modal / Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-ink/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage}
            alt="Enlarged story"
            className="max-w-full max-h-full rounded-xl shadow-lift object-contain"
          />
          <button
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-paper/20 hover:bg-paper/40 text-paper text-2xl leading-none transition-colors"
            onClick={() => setSelectedImage(null)}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
      )}
    </section>
  );
};
